import React, { useEffect, useState } from 'react';
import {
    Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
    FormControl, FormHelperText, InputLabel, MenuItem, Paper, Select, SelectChangeEvent, Table,
    TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { PosAssignableUser, PosService } from '../../services/pos.service';
import { getStoresByInstitutionId } from '../../services/store.service';
import { useToast } from '../../context/ToastContext';
import { PosStoreAssignment } from '../../interfaces/pos.interface';
import { Store } from '../../interfaces/store.interface';

// Same cap/comment as PosTerminalList.tsx's STORE_PAGE_SIZE.
const STORE_PAGE_SIZE = 100;

// Mirrors PosCashierPinService.isTrivial on the backend (all-same-digit, strictly ascending,
// strictly descending, any length 4-6) -- a client-side head start so a cashier is told before
// submitting rather than after a 400, not a substitute for the server's check (task-4 review F4:
// the previous PIN_PATTERN alone accepted values like '1234' that the real server rejects).
const isTrivialPin = (pin: string): boolean => {
    let allSame = true;
    let ascending = true;
    let descending = true;
    for (let i = 1; i < pin.length; i++) {
        const prev = pin.charCodeAt(i - 1) - 48;
        const curr = pin.charCodeAt(i) - 48;
        if (curr !== prev) allSame = false;
        if (curr !== prev + 1) ascending = false;
        if (curr !== prev - 1) descending = false;
    }
    return allSame || ascending || descending;
};

// Loose 4-6 digit shape -- the backend's authoritative validator is PosCashierPinSetRequest's
// @Pattern("\\d{4,6}") plus PosCashierPinService.isTrivial (mirrored above); this is a
// client-side head start, not a substitute for either.
const PIN_PATTERN = /^\d{4,6}$/;

const PosCashierAccess: React.FC = () => {
    const toast = useToast();

    const [stores, setStores] = useState<Store[]>([]);
    const [storeListTruncated, setStoreListTruncated] = useState(false);
    const [selectedStoreId, setSelectedStoreId] = useState('');

    const [assignments, setAssignments] = useState<PosStoreAssignment[]>([]);
    const [loadingAssignments, setLoadingAssignments] = useState(false);
    // Distinguishes "the store genuinely has no cashiers" from "the load failed" (review F1) --
    // the two must never render the same sentence, or a manager reading a failed load as an
    // empty roster cannot find the cashier they came here to deactivate.
    const [assignmentsLoadError, setAssignmentsLoadError] = useState(false);

    const [users, setUsers] = useState<PosAssignableUser[]>([]);

    const [assignOpen, setAssignOpen] = useState(false);
    const [assignUserId, setAssignUserId] = useState('');
    const [assigning, setAssigning] = useState(false);

    const [togglingId, setTogglingId] = useState<string | null>(null);

    // PIN dialog state. `pinTarget` names the cashier the dialog is for; `pinValue` is the digits
    // being typed. Both are cleared together by closePinDialog -- on submit, on Cancel, on
    // Escape, and on backdrop click -- so a cancelled dialog never leaves a PIN sitting in state
    // for the next open to show.
    const [pinTarget, setPinTarget] = useState<PosStoreAssignment | null>(null);
    const [pinValue, setPinValue] = useState('');
    const [pinError, setPinError] = useState('');
    const [settingPin, setSettingPin] = useState(false);

    useEffect(() => {
        loadStores();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadStores = async () => {
        try {
            const response = await getStoresByInstitutionId(0, STORE_PAGE_SIZE);
            const content = response.content ?? [];
            setStores(content);
            setStoreListTruncated((response.totalElements ?? content.length) > content.length);
        } catch (err) {
            toast.showApiError(err, 'Failed to load stores');
        }
    };

    const loadAssignments = async (storeId: string) => {
        try {
            setLoadingAssignments(true);
            setAssignmentsLoadError(false);
            const response = await PosService.listStoreAssignments(storeId);
            setAssignments(response);
        } catch (err) {
            setAssignmentsLoadError(true);
            toast.showApiError(err, 'Failed to load cashier assignments');
        } finally {
            setLoadingAssignments(false);
        }
    };

    // The picker's source: the assignable-users endpoint (StoreManager-readable), not the
    // InstitutionAdmin-only user directory. A failure degrades to an empty picker and must never
    // fail the assignment load, which is a different fact.
    const loadUsers = async () => {
        try {
            setUsers(await PosService.listAssignableUsers());
        } catch (err) {
            toast.showApiError(err, 'Failed to load users');
            setUsers([]);
        }
    };

    const handleStoreChange = async (event: SelectChangeEvent) => {
        const storeId = event.target.value;
        setSelectedStoreId(storeId);
        setAssignments([]);
        if (storeId) {
            await Promise.all([loadUsers(), loadAssignments(storeId)]);
        }
    };

    const openAssignDialog = () => {
        setAssignUserId('');
        setAssignOpen(true);
    };
    const closeAssignDialog = () => {
        setAssignOpen(false);
        setAssignUserId('');
    };

    const handleAssignSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!assignUserId) return;
        try {
            setAssigning(true);
            await PosService.assignCashier(selectedStoreId, assignUserId);
            closeAssignDialog();
            await loadAssignments(selectedStoreId);
        } catch (err) {
            toast.showApiError(err, 'Failed to assign cashier');
        } finally {
            setAssigning(false);
        }
    };

    const handleDeactivate = async (assignment: PosStoreAssignment) => {
        try {
            setTogglingId(assignment.id);
            await PosService.deactivateStoreAssignment(assignment.id);
            await loadAssignments(selectedStoreId);
        } catch (err) {
            toast.showApiError(err, 'Failed to deactivate assignment');
        } finally {
            setTogglingId(null);
        }
    };

    const handleReactivate = async (assignment: PosStoreAssignment) => {
        try {
            setTogglingId(assignment.id);
            await PosService.reactivateStoreAssignment(assignment.id);
            await loadAssignments(selectedStoreId);
        } catch (err) {
            toast.showApiError(err, 'Failed to reactivate assignment');
        } finally {
            setTogglingId(null);
        }
    };

    const openPinDialog = (assignment: PosStoreAssignment) => {
        setPinTarget(assignment);
        // Deliberately redundant: closePinDialog (below) already clears pinValue on every exit
        // path, so this line is never the one a test can distinguish -- it is a second guard so
        // a future close path that forgets to route through closePinDialog still cannot show a
        // stale PIN. Reviewed and reproduced by execution (task-4 review, mutation M2b): removing
        // ONLY this line leaves the suite green. Keep it anyway -- a secret is worth an
        // unreachable-by-test line of defence-in-depth.
        setPinValue('');
        setPinError('');
    };

    // The single point every dialog-close path funnels through -- Cancel, Escape, and backdrop
    // click all call this via MUI's Dialog onClose, so pinValue can never survive a cancelled
    // dialog into the next open. This clear is the one that is load-bearing: MUI keeps a Dialog's
    // children mounted through its exit transition, so an uncleared pinValue here would still be
    // findable by queryByDisplayValue immediately after a cancel or a submit.
    const closePinDialog = () => {
        setPinTarget(null);
        setPinValue('');
        setPinError('');
    };

    const handleSetPin = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!pinTarget) return;
        if (!PIN_PATTERN.test(pinValue)) {
            setPinError('PIN must be 4 to 6 digits');
            return;
        }
        if (isTrivialPin(pinValue)) {
            setPinError('PIN must not be a repeated digit or a run like 1234 or 4321');
            return;
        }
        const cashierName = pinTarget.userName;
        try {
            setSettingPin(true);
            // No storeId (review F2) -- PosCashierPin is keyed on (institutionId, userId), so
            // this PIN takes effect at every store this cashier is assigned to, not just the one
            // selected above. See setCashierPin's own comment in pos.service.ts.
            await PosService.setCashierPin(pinTarget.userId, pinValue);
            closePinDialog();
            toast.showSuccess(`PIN set for ${cashierName}`);
        } catch (err) {
            toast.showApiError(err, 'Failed to set PIN');
        } finally {
            setSettingPin(false);
        }
    };

    const assignedUserIds = new Set(assignments.map(a => a.userId));
    const assignableUsers = users.filter(u => !assignedUserIds.has(u.id));

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h5">Cashier Access</Typography>
                <Button variant="contained" disabled={!selectedStoreId} onClick={openAssignDialog}>
                    Assign Cashier
                </Button>
            </Box>

            <FormControl sx={{ minWidth: 280, mb: 3 }}>
                <InputLabel id="pos-cashier-store-label">Store</InputLabel>
                <Select
                    labelId="pos-cashier-store-label"
                    label="Store"
                    value={selectedStoreId}
                    onChange={handleStoreChange}
                >
                    {stores.map(store => (
                        <MenuItem key={store.id} value={store.id}>{store.storeName}</MenuItem>
                    ))}
                </Select>
                {storeListTruncated && (
                    <FormHelperText>
                        Showing the first {STORE_PAGE_SIZE} stores. If a store is missing, it did
                        not fit this list — contact support rather than assuming it does not exist.
                    </FormHelperText>
                )}
            </FormControl>

            {!selectedStoreId && (
                <Alert severity="info">Choose a store to see who is staffing it.</Alert>
            )}

            {selectedStoreId && loadingAssignments && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
            )}

            {selectedStoreId && !loadingAssignments && assignmentsLoadError && (
                <Alert severity="error">
                    Could not load cashier assignments for this store. This is NOT the same as
                    having no cashiers assigned — try again before assuming the roster is empty.
                </Alert>
            )}

            {selectedStoreId && !loadingAssignments && !assignmentsLoadError && (
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Cashier</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {assignments.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={3}>
                                        <Typography color="text.secondary">
                                            No cashiers assigned to this store yet.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                            {assignments.map(assignment => (
                                <TableRow key={assignment.id}>
                                    <TableCell>{assignment.userName}</TableCell>
                                    <TableCell>
                                        <Chip
                                            size="small"
                                            color={assignment.isActive ? 'success' : 'default'}
                                            label={assignment.isActive ? 'Active' : 'Inactive'}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Button size="small" onClick={() => openPinDialog(assignment)}>
                                            Set / Reset PIN
                                        </Button>
                                        {assignment.isActive ? (
                                            <Button
                                                size="small"
                                                color="warning"
                                                disabled={togglingId === assignment.id}
                                                onClick={() => handleDeactivate(assignment)}
                                            >
                                                Deactivate
                                            </Button>
                                        ) : (
                                            <Button
                                                size="small"
                                                color="success"
                                                disabled={togglingId === assignment.id}
                                                onClick={() => handleReactivate(assignment)}
                                            >
                                                Reactivate
                                            </Button>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Assign-cashier dialog */}
            <Dialog open={assignOpen} onClose={closeAssignDialog} fullWidth maxWidth="xs">
                <form onSubmit={handleAssignSubmit}>
                    <DialogTitle>Assign Cashier</DialogTitle>
                    <DialogContent sx={{ pt: 1 }}>
                        <FormControl fullWidth required>
                            <InputLabel id="pos-cashier-assign-user-label">User</InputLabel>
                            <Select
                                labelId="pos-cashier-assign-user-label"
                                label="User"
                                value={assignUserId}
                                onChange={e => setAssignUserId(e.target.value)}
                            >
                                {assignableUsers.map(u => (
                                    <MenuItem key={u.id} value={u.id}>{u.username}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={closeAssignDialog}>Cancel</Button>
                        <Button type="submit" variant="contained" disabled={assigning || !assignUserId}>
                            Assign
                        </Button>
                    </DialogActions>
                </form>
            </Dialog>

            {/* Set/Reset PIN dialog -- write-only. There is no backend endpoint that can return a
                PIN (see PosCashierPinController javadoc), so this screen never has one to show,
                and never can tell whether this cashier already has one -- hence one unconditional
                "Set / Reset PIN" label everywhere (review F5), rather than picking between "Set"
                and "Reset" off a signal (assignment.isActive) that has nothing to do with whether
                a PIN exists. */}
            <Dialog open={Boolean(pinTarget)} onClose={closePinDialog} fullWidth maxWidth="xs">
                <form onSubmit={handleSetPin}>
                    <DialogTitle>Set / Reset PIN — {pinTarget?.userName}</DialogTitle>
                    <DialogContent sx={{ pt: 1 }}>
                        <Alert severity="info" sx={{ mb: 2 }}>
                            This PIN applies to {pinTarget?.userName} at every store they are
                            assigned to, not just the store selected above.
                        </Alert>
                        <TextField
                            label="PIN"
                            type="password"
                            autoComplete="off"
                            value={pinValue}
                            onChange={e => {
                                setPinError('');
                                setPinValue(e.target.value);
                            }}
                            error={Boolean(pinError)}
                            helperText={pinError || '4 to 6 digits -- not a repeat or a run'}
                            required
                            autoFocus
                            fullWidth
                            // getByLabelText is ambiguous here -- the dialog's aria-labelledby
                            // chains up to the "Set / Reset PIN — <cashier>" title, which also
                            // matches /pin/i. A stable test id avoids that false multi-match.
                            // inputMode must be set inside inputProps -- TextField's own inputMode
                            // prop lands on the wrapping FormControl div, not the <input>, and it
                            // is the <input>'s inputmode that puts a numeric keypad on a counter's
                            // touchscreen.
                            inputProps={{ 'data-testid': 'pos-cashier-pin-input', inputMode: 'numeric' }}
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={closePinDialog}>Cancel</Button>
                        <Button type="submit" variant="contained" disabled={settingPin}>Save</Button>
                    </DialogActions>
                </form>
            </Dialog>
        </Box>
    );
};

export default PosCashierAccess;
